// TerraForge wire protocol (NET-1). One byte-tagged message per frame; the same
// codec runs on the Node server and in the browser replica (netproto is renderer-
// and DOM-free, imported by `server/**` and `src/client/net/**` only — never by the
// sim). Every message is validated by a zod schema (structural contract) AND has a
// hand binary codec (compact wire form). `encode`/`decode` are inverses — the fuzz
// test round-trips 10k random messages through both and asserts deep equality.
//
// Wire conventions: little-endian; ids/lengths/state-ids are unsigned LEB128
// varints; signed block coords are zig-zag varints; world Y is stored as `y+64`
// (range −64..319 → 0..383); all game-world floats (positions, look, velocity) are
// f64 so the round-trip is exact and reconciliation keeps full precision.
import { z } from 'zod';
import { ByteWriter, ByteReader } from './bytes';

/** Bumped whenever the wire form changes incompatibly; checked at Hello.
 *  v2 (C6): adds the `Pickup` grant for replicated item-drop collection.
 *  v3 (C5): the `Attack` frame carries the attacker's held item (weapon-aware
 *  server damage + per-swing cooldown).
 *  v4 (C7): adds the `Dimension` message for multi-dim portal travel.
 *  v8 (SCR-19, ARCH §27.14): adds op-only `ContentPush` (C2S) and `ContentApplied`
 *  (S2C) — a live script/collection push to a running server. A v7 client is refused by
 *  the Hello check below; there is no partial-support path.
 *  v9 (HER, ARCH §28.12): `Spawn` entities carry an optional `genome`/`name` (one presence
 *  byte each entity); new S2C `CropTint`/`EntityName`/`NotebookUpdate` and C2S
 *  `Name`/`Record`.
 *  v10 (EDU, ARCH §29.13): `Players` entries carry an optional `role`/`hand` (one
 *  presence byte each entry); new S2C `Lesson` (freeze/eyes-up/watch control) and
 *  `LessonUpdate` (a result row); new C2S `Hand` and `QuizSubmit`.
 *  v11 (COW, ARCH §30.7): declares every Part XI frame at once — the share frames
 *  stay inert until M57 (D-TF-195). New S2C `BlockText`, `ShareState`; new C2S
 *  `BlockTextEdit`, `ShareStart`, `ShareStop`, `ShareJoin`, `ShareLeave`; `Signal`
 *  travels BOTH ways (the server relays it verbatim between exactly two peers,
 *  never validating it as state — §30.7's one exception to "every frame is
 *  either C2S or S2C"). `Players` entries carry an optional `status`. */
export const PROTOCOL_VERSION = 11;

/** World Y is biased by this so it encodes as a non-negative varint (−64..319). */
const Y_BIAS = 64;

// ── Message tags (single u8 space across both directions) ────────────────────
export const Tag = {
  // client → server
  Hello: 1,
  Input: 2,
  Edit: 3,
  Txn: 4,
  Command: 5,
  Chat: 6,
  Ping: 7,
  Attack: 8,
  /** Board / leave a vehicle (NET mount replication, M19 remainder). */
  Mount: 9,
  /** Light a TNT block (NET primed-TNT replication, M28 remainder). */
  Ignite: 10,
  /** Place a boat / minecart (NET vehicle replication, M28 remainder). */
  Vehicle: 11,
  /** Push a content patch — scripts + mob script fields — to a running server (v8,
   *  §27.14). Op-only; the server re-validates everything and applies at a tick
   *  boundary. */
  ContentPush: 12,
  /** Name a mob with a held name tag (v9, §28.9). The server re-validates. */
  Name: 13,
  /** Record a mob or a genetic crop in the field notebook (v9, §28.11). */
  Record: 14,
  /** Claim a genetic crop's stalk-colour quintile at a cell (v9, §28.10, D-TF-251).
   *  Crop block entities do not replicate (D-TF-136), so the client — the only side
   *  that ever decodes the actual genome — reports the one number the tint needs,
   *  the same trust the server already gives `Attack.item`/`Name.text` (it owns no
   *  player inventory, D-TF-249). Sent once, right after the block entity is
   *  created; harvest needs no reply — the server infers the clear from the
   *  ordinary `Edit` that breaks the cell. */
  Plant: 15,
  /** Raise or lower a hand (v10, §29.8, D-TF-186). */
  Hand: 16,
  /** Submit a quiz attempt for the server to grade and record (v10, §29.5). */
  QuizSubmit: 17,
  /** Edit a sign/sticky_note/task_board/timer's text payload (v11, §30.2). */
  BlockTextEdit: 18,
  // server → client
  Welcome: 20,
  Section: 21,
  Patch: 22,
  Unsub: 23,
  Spawn: 24,
  Move: 25,
  Despawn: 26,
  Correct: 27,
  TxnResult: 28,
  ChatMsg: 29,
  Players: 30,
  TimeWeather: 31,
  Pong: 32,
  Kick: 33,
  Pickup: 34,
  Dimension: 35,
  /** Authoritative seat roster: who is riding what, and in which seat. */
  Riders: 36,
  /** A content push was applied (v8, §27.14) — broadcast to everyone. */
  ContentApplied: 37,
  /** Stalk-colour quintiles for a section's genetic crops (v9, §28.10, D-TF-136). */
  CropTint: 38,
  /** A mob was named (v9, §28.9). */
  EntityName: 39,
  /** Notebook records to upsert (v9, §28.11) — the server's auto-records and deaths. */
  NotebookUpdate: 40,
  /** An instructor control aimed at this player: freeze, eyes-up, watch (v10, §29.8). */
  Lesson: 41,
  /** A lesson result row (v10, §29.4): the recipient's own, or — to an instructor — a
   *  student's, named by `player`. */
  LessonUpdate: 42,
  /** A block's text payload (sign/sticky_note/task_board/timer, v11, §30.2). */
  BlockText: 43,
  /** Share lifecycle state for a `shared_screen` (v11, §30.6) — inert until M57. */
  ShareState: 44,
  /** WebRTC signaling, relayed opaque between exactly two peers (v11, §30.6) —
   *  travels BOTH directions under this one tag; inert until M57. */
  Signal: 45,
  /** Start sharing the screen at (x,y,z) (v11, §30.6) — inert until M57. */
  ShareStart: 46,
  /** Stop the share this player owns (v11, §30.6) — inert until M57. */
  ShareStop: 47,
  /** Join as a viewer of the screen at (x,y,z) (v11, §30.6) — inert until M57. */
  ShareJoin: 48,
  /** Leave a screen this player was viewing (v11, §30.6) — inert until M57. */
  ShareLeave: 49,
} as const;
export type Tag = (typeof Tag)[keyof typeof Tag];

// ── Transaction op kinds (NET-6 TxnOp.kind) ──────────────────────────────────
export const TxnKind = {
  /** Take `count` from container slot `a` (b unused). */
  Take: 0,
  /** Put `count` into container slot `a` from the requester (b unused). */
  Put: 1,
  /** Move `count` between container slots `a`→`b`. */
  Move: 2,
} as const;

// ── Input button bitfield (NET-1 InputFrame `buttons`) ───────────────────────
export const Btn = {
  Forward: 1 << 0,
  Back: 1 << 1,
  Left: 1 << 2,
  Right: 1 << 3,
  Jump: 1 << 4,
  Sneak: 1 << 5,
  Sprint: 1 << 6,
  Attack: 1 << 7,
  Use: 1 << 8,
} as const;

// ── Shared sub-shapes ────────────────────────────────────────────────────────
export const zVec3 = z.object({ x: z.number(), y: z.number(), z: z.number() });
export const zTxnOp = z.object({
  /** 0 move · 1 split · 2 merge · 3 swap · 4 shift-move · 5 drop (PLR-1 Transaction). */
  kind: z.number().int().min(0).max(15),
  container: z.string(),
  a: z.number().int(),
  b: z.number().int(),
  count: z.number().int().min(0),
});
export type TxnOp = z.infer<typeof zTxnOp>;

const zMetaWire = z.object({
  id: z.string(),
  name: z.string(),
  seed: z.string(),
  worldType: z.enum(['normal', 'flat', 'atlas', 'earth']),
  gameMode: z.enum(['survival', 'creative']),
  difficulty: z.enum(['peaceful', 'easy', 'normal', 'hard']),
  hardcore: z.boolean(),
  flatPreset: z.string(),
});
export type MetaWire = z.infer<typeof zMetaWire>;

const zSpawnPos = z.object({
  x: z.number(), y: z.number(), z: z.number(), yaw: z.number(), pitch: z.number(), dim: z.string(),
});

// ── Message schemas (discriminant `t` = tag) ─────────────────────────────────
export const zHello = z.object({
  t: z.literal(Tag.Hello), protocol: z.number().int(), name: z.string(), hue: z.number().int().min(0).max(360),
  viewRadius: z.number().int().min(2).max(32), token: z.string(),
});
export const zInput = z.object({
  t: z.literal(Tag.Input), seq: z.number().int().min(0), buttons: z.number().int().min(0),
  yaw: z.number(), pitch: z.number(), hotbar: z.number().int().min(0).max(8),
});
export const zEdit = z.object({
  t: z.literal(Tag.Edit), seq: z.number().int().min(0),
  x: z.number().int(), y: z.number().int(), z: z.number().int(), state: z.number().int().min(0), place: z.boolean(),
});
export const zTxn = z.object({ t: z.literal(Tag.Txn), id: z.number().int().min(0), ops: z.array(zTxnOp) });
export const zCommand = z.object({ t: z.literal(Tag.Command), text: z.string() });
export const zChat = z.object({ t: z.literal(Tag.Chat), text: z.string() });
export const zPing = z.object({ t: z.literal(Tag.Ping), time: z.number() });
export const zAttack = z.object({
  t: z.literal(Tag.Attack), seq: z.number().int().min(0), targetId: z.number().int().min(0),
  /** The attacker's held item id (0 = fist) → server weapon damage + cooldown (C5). */
  item: z.number().int().min(0),
});

/** Client → server: board `vehicleId` in `seat`, or dismount (`vehicleId` 0). The
 *  server owns the seat roster — a client cannot decide it is on something. */
export const zMount = z.object({
  t: z.literal(Tag.Mount), seq: z.number().int().min(0),
  vehicleId: z.number().int().min(0), seat: z.number().int().min(0).max(7),
});

/** Client → server: light the TNT block at (x,y,z). The server validates reach and
 *  that the block really is TNT, then owns the primed entity from there — the fuse,
 *  the fall, the blast and the chain reaction are all authoritative. */
export const zIgnite = z.object({
  t: z.literal(Tag.Ignite), seq: z.number().int().min(0),
  x: z.number().int(), y: z.number().int(), z: z.number().int(),
});
/** Client → server: put a vehicle down at (x,y,z). The server validates reach and
 *  the footing (water for a boat, a rail for a cart) and owns it from there — its
 *  physics, its passengers and its pose are all authoritative. */
export const zVehicle = z.object({
  t: z.literal(Tag.Vehicle), seq: z.number().int().min(0),
  kind: z.string(), x: z.number(), y: z.number(), z: z.number(), yaw: z.number(),
});
export const zWelcome = z.object({
  t: z.literal(Tag.Welcome), playerId: z.number().int().min(0), tick: z.number().int().min(0),
  meta: zMetaWire, spawn: zSpawnPos, viewRadius: z.number().int().min(2).max(32),
});
export const zSection = z.object({
  t: z.literal(Tag.Section), cx: z.number().int(), cz: z.number().int(), sy: z.number().int().min(0).max(23),
  palette: z.array(z.number().int().min(0)), indices: z.array(z.number().int().min(0)),
});
export const zPatch = z.object({
  t: z.literal(Tag.Patch), cx: z.number().int(), cz: z.number().int(), sy: z.number().int().min(0).max(23),
  ops: z.array(z.object({ li: z.number().int().min(0).max(4095), state: z.number().int().min(0) })),
});
export const zUnsub = z.object({ t: z.literal(Tag.Unsub), cx: z.number().int(), cz: z.number().int() });
export const zSpawn = z.object({
  t: z.literal(Tag.Spawn),
  entities: z.array(z.object({
    id: z.number().int().min(0), kind: z.string(), x: z.number(), y: z.number(), z: z.number(), yaw: z.number(),
    /** v9 (§28.12): the mob's genome (base64), so a client can express its tint/parts/scale. */
    genome: z.string().optional(),
    /** v9 (§28.9): its name-tag name. */
    name: z.string().optional(),
  })),
});
export const zMove = z.object({
  t: z.literal(Tag.Move),
  entities: z.array(z.object({
    id: z.number().int().min(0), x: z.number(), y: z.number(), z: z.number(), yaw: z.number(), pitch: z.number(),
  })),
});
export const zDespawn = z.object({ t: z.literal(Tag.Despawn), ids: z.array(z.number().int().min(0)) });
export const zCorrect = z.object({
  t: z.literal(Tag.Correct), seq: z.number().int().min(0),
  x: z.number(), y: z.number(), z: z.number(), vx: z.number(), vy: z.number(), vz: z.number(),
});
export const zTxnResult = z.object({ t: z.literal(Tag.TxnResult), id: z.number().int().min(0), ok: z.boolean() });
export const zChatMsg = z.object({ t: z.literal(Tag.ChatMsg), from: z.string(), text: z.string() });
export const zPlayers = z.object({
  t: z.literal(Tag.Players),
  list: z.array(z.object({
    id: z.number().int().min(0), name: z.string(), hue: z.number().int().min(0).max(360),
    /** v10 (§29.13): the player's world role (`owner|instructor|editor|visitor|guest`). */
    role: z.string().optional(),
    /** v10: raised-hand queue number (1 = first), absent when the hand is down. */
    hand: z.number().int().min(1).optional(),
    /** v11 (§30.4, COW-5): presence status. Session-only, never sim state. */
    status: z.enum(['available', 'focus', 'call']).optional(),
  })),
});
export const zTimeWeather = z.object({
  t: z.literal(Tag.TimeWeather), dayTime: z.number().int().min(0), raining: z.boolean(), thundering: z.boolean(),
});
export const zPong = z.object({ t: z.literal(Tag.Pong), time: z.number() });
export const zKick = z.object({ t: z.literal(Tag.Kick), reason: z.string() });
/** Server → the collecting client: an item-drop it picked up enters that client's
 *  inventory (NET, C6). Only the granted player receives it, so no dupe. */
export const zPickup = z.object({ t: z.literal(Tag.Pickup), item: z.number().int().min(0), count: z.number().int().min(1) });
/** Server → one client: switch your replica to `dim` and reposition (portal travel,
 *  C7). The client re-generates that dimension's pristine terrain from the shared
 *  seed and applies its streamed edited sections. */
export const zDimension = z.object({
  t: z.literal(Tag.Dimension), dim: z.string(), x: z.number(), y: z.number(), z: z.number(),
});

/** Server → all: the whole seat roster, resent whenever it changes. Small enough
 *  to send whole (a world has a handful of riders), and a full snapshot means a
 *  late joiner and a reconnecting client agree with no replay.
 *
 *  `riderId` is a wire entity id, which is why it is not called `playerId` any
 *  more: a MOB can hold a seat too (a zombie shoved into a boat), and mob ids,
 *  vehicle ids and player ids all share one wire space. */
export const zRiders = z.object({
  t: z.literal(Tag.Riders),
  list: z.array(z.object({
    riderId: z.number().int().min(0),
    vehicleId: z.number().int().min(0),
    seat: z.number().int().min(0).max(7),
  })),
});

/** Client → server (v8, §27.14): push a content patch to a running server.
 *  `patch` is the JSON text of a `ContentPatch` (below) — carried as a string so the
 *  wire form never has to learn the patch's shape, and so the server parses it as the
 *  UNTRUSTED input it is rather than receiving pre-typed fields. Op-only (E-49). */
export const zContentPush = z.object({
  t: z.literal(Tag.ContentPush), collection: z.string(), version: z.string(), patch: z.string(),
});
/** Server → all (v8): a push landed. `hash` is the sha-256 of the applied content, so a
 *  client can tell two pushes apart without receiving their source (behaviour stays
 *  server-side, §27.4). */
export const zContentApplied = z.object({
  t: z.literal(Tag.ContentApplied), collection: z.string(), version: z.string(), hash: z.string(),
});

// ── Heredity frames (v9, ARCH §28.12) ────────────────────────────────────────
const zAnimalRecord = z.object({
  id: z.number().int().min(0), species: z.string(), name: z.string().optional(), genome: z.string(),
  parents: z.tuple([z.number().int().min(0), z.number().int().min(0)]), born: z.number().int().min(0),
  died: z.number().int().min(0).optional(), cause: z.enum(['old_age', 'other']).optional(),
});
const zCropRecord = z.object({
  x: z.number().int(), y: z.number().int(), z: z.number().int(), crop: z.string(), genome: z.string(),
  harvests: z.number().int().min(0),
});
/** S2C: stalk-colour quintiles for genetic crop cells in one section. `q` 0..4 paints a
 *  cell; `q = 5` clears it (§28.12). */
export const zCropTint = z.object({
  t: z.literal(Tag.CropTint), cx: z.number().int(), cz: z.number().int(), sy: z.number().int(),
  cells: z.array(z.object({ li: z.number().int().min(0).max(4095), q: z.number().int().min(0).max(5) })),
});
/** S2C: a mob now carries this name. */
export const zEntityName = z.object({ t: z.literal(Tag.EntityName), id: z.number().int().min(0), name: z.string() });
/** S2C: notebook records to upsert into this player's notebook. */
export const zNotebookUpdate = z.object({
  t: z.literal(Tag.NotebookUpdate), animals: z.array(zAnimalRecord).optional(), crops: z.array(zCropRecord).optional(),
});
/** C2S: name mob `id` `text` with the held name tag.
 *
 *  `item` is the sender's HELD item id, exactly as `Attack` reports the weapon it swung
 *  (C5): the server owns no player inventory, so "a name tag in the sender's held slot" is
 *  the client's claim, re-checked against the registry and against reach and liveness
 *  server-side (D-TF-249). The client consumes its own tag. */
export const zName = z.object({
  t: z.literal(Tag.Name), id: z.number().int().min(0), text: z.string(), item: z.number().int().min(0),
});
/** C2S: record a mob (`id`) or a genetic crop cell (`x,y,z`) in the notebook. */
export const zRecord = z.object({
  t: z.literal(Tag.Record), kind: z.enum(['animal', 'crop']),
  id: z.number().int().min(0).optional(), x: z.number().int().optional(), y: z.number().int().optional(), z: z.number().int().optional(),
});
/** C2S: a genetic crop's stalk-colour quintile at a cell (D-TF-251). `q` 0..4 —
 *  unlike `CropTint`'s own `q`, there is no `5` here; a claim always paints. */
export const zPlant = z.object({
  t: z.literal(Tag.Plant), x: z.number().int(), y: z.number().int(), z: z.number().int(), q: z.number().int().min(0).max(4),
});

// ── Education frames (v10, ARCH §29.13) ──────────────────────────────────────
/** The control kinds a `Lesson` frame carries, in wire order (a u8 index). */
export const LESSON_KINDS = ['freeze', 'unfreeze', 'eyesUp', 'eyesDown', 'watch', 'unwatch'] as const;
export type LessonKind = (typeof LESSON_KINDS)[number];
/** S2C: freeze / eyes-up / watch control for this player (§29.8). `text` is the
 *  instructor's eyes-up line. */
export const zLesson = z.object({ t: z.literal(Tag.Lesson), kind: z.enum(LESSON_KINDS), text: z.string().optional() });
/** One §29.6 `LessonResult` on the wire. */
const zLessonResultWire = z.object({
  started: z.number().int().min(0),
  objectives: z.record(z.string(), z.number().int().min(0).nullable()),
  counts: z.array(z.tuple([z.string(), z.number().int().min(0)])),
  quiz: z.object({
    best: z.number().int().min(0), n: z.number().int().min(0), attempts: z.number().int().min(0), last: z.number().int().min(0),
  }).optional(),
  ticksOnTask: z.number().int().min(0),
  completed: z.number().int().min(0).optional(),
});
export type LessonResultWire = z.infer<typeof zLessonResultWire>;
/** S2C: a result row for `lesson`. Absent `player` = the recipient's own row; present =
 *  that student's row, sent to instructors for the roster (§29.4 "pushed to that student
 *  and to every instructor's roster", D-TF-266). */
export const zLessonUpdate = z.object({
  t: z.literal(Tag.LessonUpdate), lesson: z.string(), result: zLessonResultWire, player: z.number().int().min(0).optional(),
});
/** C2S: raise (`up`) or lower this player's hand (§29.8). */
export const zHand = z.object({ t: z.literal(Tag.Hand), up: z.boolean() });
/** One quiz answer: a choice index / numeric value, or a Punnett grid (§29.5). */
const zQuizAnswerWire = z.union([z.number(), z.array(z.array(z.string()))]);
/** C2S: a quiz attempt; the server grades and records it (§29.5, E-60). */
export const zQuizSubmit = z.object({ t: z.literal(Tag.QuizSubmit), lesson: z.string(), answers: z.array(zQuizAnswerWire) });

// ── Co-working frames (v11, ARCH §30.7) ──────────────────────────────────────
/** The four block kinds `BlockText`/`BlockTextEdit` carry (§30.2). `sign` reads/
 *  writes the pre-existing host-side maps (client `Map<cellKey,string[]>`,
 *  server's new per-world side table) — never a sim block entity (D-TF-185).
 *  The other three are a real sim block entity's data, JSON-encoded. */
export const BLOCK_TEXT_KINDS = ['sign', 'sticky_note', 'task_board', 'timer'] as const;
export type BlockTextKind = (typeof BLOCK_TEXT_KINDS)[number];
/** S2C: this cell's text payload — a sign's lines (JSON string array) or a
 *  sticky_note/task_board/timer's block-entity data (JSON object), always
 *  carried as one JSON string so the wire never has to learn either shape. */
export const zBlockText = z.object({
  t: z.literal(Tag.BlockText), x: z.number().int(), y: z.number().int(), z: z.number().int(),
  kind: z.enum(BLOCK_TEXT_KINDS), payload: z.string(),
});
/** C2S: edit a cell's text payload; the server validates reach/role/kind/cap
 *  before applying and broadcasting (§30.2). `linked` (Hangar §39.7, M107.2) is a
 *  `task_board`-only claim that this cell has an active Reminders link, which the
 *  server never trusts without its own `POST /world/board-link` record — present
 *  so an out-of-reach incremental sync write can ask for the reach waiver at all. */
export const zBlockTextEdit = z.object({
  t: z.literal(Tag.BlockTextEdit), x: z.number().int(), y: z.number().int(), z: z.number().int(),
  kind: z.enum(BLOCK_TEXT_KINDS), payload: z.string(), linked: z.boolean().optional(),
});

/** S2C: share lifecycle for the `shared_screen` at (x,y,z) (§30.6) — inert until
 *  M57 (no handler emits or consumes it before then; the frame is only DECLARED
 *  at v11, D-TF-195). `ice` carries the ICE server list (incl. a time-limited
 *  TURN credential, D-TF-196) only when live and only to an authorized peer. */
export const zShareState = z.object({
  t: z.literal(Tag.ShareState), x: z.number().int(), y: z.number().int(), z: z.number().int(),
  sharer: z.number().int().min(0), live: z.boolean(),
  ice: z.array(z.object({ urls: z.string(), username: z.string().optional(), credential: z.string().optional() })).optional(),
});
/** Both ways: opaque WebRTC signaling the server relays between exactly two
 *  peers, never validated as state (§30.7's one exception) — inert until M57. */
export const zSignal = z.object({
  t: z.literal(Tag.Signal), from: z.number().int().min(0), to: z.number().int().min(0), payload: z.string(),
});
/** C2S: start sharing the screen at (x,y,z) — inert until M57. */
export const zShareStart = z.object({ t: z.literal(Tag.ShareStart), x: z.number().int(), y: z.number().int(), z: z.number().int() });
/** C2S: stop the share this player owns — inert until M57. */
export const zShareStop = z.object({ t: z.literal(Tag.ShareStop), x: z.number().int(), y: z.number().int(), z: z.number().int() });
/** C2S: join as a viewer of the screen at (x,y,z) — inert until M57. */
export const zShareJoin = z.object({ t: z.literal(Tag.ShareJoin), x: z.number().int(), y: z.number().int(), z: z.number().int() });
/** C2S: leave a screen this player was viewing — inert until M57. */
export const zShareLeave = z.object({ t: z.literal(Tag.ShareLeave), x: z.number().int(), y: z.number().int(), z: z.number().int() });

/** The body a `ContentPush.patch` carries (§27.14's "overlay patch"), restricted to what
 *  a RUNNING server can take without a restart: script SOURCES, and the two mob fields
 *  that point at them. A block/item/recipe change would re-intern registry state that
 *  every connected client already holds, so it is not pushable (D-TF-237). `null`
 *  clears a field back to "no script" / the default cadence. */
export const zContentPatch = z.object({
  scripts: z.record(z.string(), z.string()).optional(),
  mobs: z.record(z.string(), z.object({
    script: z.string().nullable().optional(),
    scriptHz: z.union([z.literal(5), z.literal(20)]).nullable().optional(),
  }).strict()).optional(),
}).strict();
export type ContentPatch = z.infer<typeof zContentPatch>;

export const zNetMessage = z.discriminatedUnion('t', [
  zHello, zInput, zEdit, zTxn, zCommand, zChat, zPing, zAttack, zMount, zIgnite, zVehicle, zContentPush, zName, zRecord, zPlant,
  zHand, zQuizSubmit, zBlockTextEdit, zShareStart, zShareStop, zShareJoin, zShareLeave, zSignal,
  zWelcome, zSection, zPatch, zUnsub, zSpawn, zMove, zDespawn, zCorrect, zTxnResult, zChatMsg, zPlayers, zTimeWeather, zPong, zKick, zPickup, zDimension, zRiders,
  zContentApplied, zCropTint, zEntityName, zNotebookUpdate, zLesson, zLessonUpdate, zBlockText, zShareState,
]);
export type NetMessage = z.infer<typeof zNetMessage>;

// Handy per-direction aliases for call sites. `Signal` travels both ways
// (§30.7's relay exception), so it appears in both.
export type C2S = z.infer<typeof zHello> | z.infer<typeof zInput> | z.infer<typeof zEdit> | z.infer<typeof zTxn>
  | z.infer<typeof zCommand> | z.infer<typeof zChat> | z.infer<typeof zPing> | z.infer<typeof zAttack>
  | z.infer<typeof zMount> | z.infer<typeof zIgnite> | z.infer<typeof zVehicle> | z.infer<typeof zContentPush>
  | z.infer<typeof zName> | z.infer<typeof zRecord> | z.infer<typeof zPlant>
  | z.infer<typeof zHand> | z.infer<typeof zQuizSubmit>
  | z.infer<typeof zBlockTextEdit> | z.infer<typeof zShareStart> | z.infer<typeof zShareStop>
  | z.infer<typeof zShareJoin> | z.infer<typeof zShareLeave> | z.infer<typeof zSignal>;
export type S2C = z.infer<typeof zWelcome> | z.infer<typeof zSection> | z.infer<typeof zPatch> | z.infer<typeof zUnsub>
  | z.infer<typeof zSpawn> | z.infer<typeof zMove> | z.infer<typeof zDespawn> | z.infer<typeof zCorrect>
  | z.infer<typeof zTxnResult> | z.infer<typeof zChatMsg> | z.infer<typeof zPlayers> | z.infer<typeof zTimeWeather>
  | z.infer<typeof zPong> | z.infer<typeof zKick> | z.infer<typeof zPickup> | z.infer<typeof zDimension>
  | z.infer<typeof zRiders> | z.infer<typeof zContentApplied> | z.infer<typeof zCropTint>
  | z.infer<typeof zEntityName> | z.infer<typeof zNotebookUpdate>
  | z.infer<typeof zLesson> | z.infer<typeof zLessonUpdate>
  | z.infer<typeof zBlockText> | z.infer<typeof zShareState> | z.infer<typeof zSignal>;
export type PlayerEntryWire = z.infer<typeof zPlayers>['list'][number];
export type AnimalRecordWire = z.infer<typeof zAnimalRecord>;
export type CropRecordWire = z.infer<typeof zCropRecord>;

// ── Codec ────────────────────────────────────────────────────────────────────
function writeMeta(w: ByteWriter, m: MetaWire): void {
  w.str(m.id).str(m.name).str(m.seed).str(m.worldType).str(m.gameMode).str(m.difficulty).bool(m.hardcore).str(m.flatPreset);
}
function readMeta(r: ByteReader): MetaWire {
  return {
    id: r.str(), name: r.str(), seed: r.str(),
    worldType: r.str() as MetaWire['worldType'], gameMode: r.str() as MetaWire['gameMode'],
    difficulty: r.str() as MetaWire['difficulty'], hardcore: r.bool(), flatPreset: r.str(),
  };
}

/** Serialize a message to its wire bytes (tag byte + payload). */
export function encode(msg: NetMessage): Uint8Array {
  const w = new ByteWriter();
  w.u8(msg.t);
  switch (msg.t) {
    case Tag.Hello:
      w.varint(msg.protocol).str(msg.name).varint(msg.hue).varint(msg.viewRadius).str(msg.token); break;
    case Tag.Input:
      w.varint(msg.seq).varint(msg.buttons).f64(msg.yaw).f64(msg.pitch).u8(msg.hotbar); break;
    case Tag.Edit:
      w.varint(msg.seq).svarint(msg.x).varint(msg.y + Y_BIAS).svarint(msg.z).varint(msg.state).bool(msg.place); break;
    case Tag.Txn:
      w.varint(msg.id).varint(msg.ops.length);
      for (const op of msg.ops) w.u8(op.kind).str(op.container).svarint(op.a).svarint(op.b).varint(op.count);
      break;
    case Tag.Command: w.str(msg.text); break;
    case Tag.Chat: w.str(msg.text); break;
    case Tag.Ping: w.f64(msg.time); break;
    case Tag.Attack: w.varint(msg.seq).varint(msg.targetId).varint(msg.item); break;
    case Tag.Mount: w.varint(msg.seq).varint(msg.vehicleId).u8(msg.seat); break;
    case Tag.Ignite: w.varint(msg.seq).svarint(msg.x).varint(msg.y + Y_BIAS).svarint(msg.z); break;
    case Tag.Vehicle: w.varint(msg.seq).str(msg.kind).f64(msg.x).f64(msg.y).f64(msg.z).f64(msg.yaw); break;
    case Tag.ContentPush: w.str(msg.collection).str(msg.version).str(msg.patch); break;
    case Tag.Name: w.varint(msg.id).str(msg.text).varint(msg.item); break;
    case Tag.Record:
      w.u8(msg.kind === 'animal' ? 0 : 1)
        .u8((msg.id !== undefined ? 1 : 0) | (msg.x !== undefined ? 2 : 0) | (msg.y !== undefined ? 4 : 0) | (msg.z !== undefined ? 8 : 0));
      if (msg.id !== undefined) w.varint(msg.id);
      if (msg.x !== undefined) w.svarint(msg.x);
      if (msg.y !== undefined) w.svarint(msg.y);
      if (msg.z !== undefined) w.svarint(msg.z);
      break;
    case Tag.Plant: w.svarint(msg.x).varint(msg.y + Y_BIAS).svarint(msg.z).u8(msg.q); break;
    case Tag.Hand: w.bool(msg.up); break;
    case Tag.QuizSubmit:
      w.str(msg.lesson).varint(msg.answers.length);
      for (const a of msg.answers) writeQuizAnswer(w, a);
      break;
    case Tag.Welcome:
      w.varint(msg.playerId).varint(msg.tick);
      writeMeta(w, msg.meta);
      w.f64(msg.spawn.x).f64(msg.spawn.y).f64(msg.spawn.z).f64(msg.spawn.yaw).f64(msg.spawn.pitch).str(msg.spawn.dim);
      w.varint(msg.viewRadius);
      break;
    case Tag.Section:
      w.svarint(msg.cx).svarint(msg.cz).u8(msg.sy).u16array(msg.palette).u16array(msg.indices); break;
    case Tag.Patch:
      w.svarint(msg.cx).svarint(msg.cz).u8(msg.sy).varint(msg.ops.length);
      for (const op of msg.ops) w.varint(op.li).varint(op.state);
      break;
    case Tag.Unsub: w.svarint(msg.cx).svarint(msg.cz); break;
    case Tag.Spawn:
      w.varint(msg.entities.length);
      for (const e of msg.entities) {
        w.varint(e.id).str(e.kind).f64(e.x).f64(e.y).f64(e.z).f64(e.yaw);
        // v9: one presence byte (bit 0 genome, bit 1 name), then the present strings.
        w.u8((e.genome !== undefined ? 1 : 0) | (e.name !== undefined ? 2 : 0));
        if (e.genome !== undefined) w.str(e.genome);
        if (e.name !== undefined) w.str(e.name);
      }
      break;
    case Tag.Move:
      w.varint(msg.entities.length);
      for (const e of msg.entities) w.varint(e.id).f64(e.x).f64(e.y).f64(e.z).f64(e.yaw).f64(e.pitch);
      break;
    case Tag.Despawn:
      w.varint(msg.ids.length);
      for (const id of msg.ids) w.varint(id);
      break;
    case Tag.Correct:
      w.varint(msg.seq).f64(msg.x).f64(msg.y).f64(msg.z).f64(msg.vx).f64(msg.vy).f64(msg.vz); break;
    case Tag.TxnResult: w.varint(msg.id).bool(msg.ok); break;
    case Tag.ChatMsg: w.str(msg.from).str(msg.text); break;
    case Tag.Players:
      w.varint(msg.list.length);
      for (const p of msg.list) {
        w.varint(p.id).str(p.name).varint(p.hue);
        // v10/v11: one presence byte (bit 0 role, bit 1 hand, bit 2 status), then
        // the present values.
        w.u8((p.role !== undefined ? 1 : 0) | (p.hand !== undefined ? 2 : 0) | (p.status !== undefined ? 4 : 0));
        if (p.role !== undefined) w.str(p.role);
        if (p.hand !== undefined) w.varint(p.hand);
        if (p.status !== undefined) w.str(p.status);
      }
      break;
    case Tag.TimeWeather: w.varint(msg.dayTime).bool(msg.raining).bool(msg.thundering); break;
    case Tag.Pong: w.f64(msg.time); break;
    case Tag.Kick: w.str(msg.reason); break;
    case Tag.Pickup: w.varint(msg.item).varint(msg.count); break;
    case Tag.Dimension: w.str(msg.dim).f64(msg.x).f64(msg.y).f64(msg.z); break;
    case Tag.Riders:
      w.varint(msg.list.length);
      for (const rd of msg.list) w.varint(rd.riderId).varint(rd.vehicleId).u8(rd.seat);
      break;
    case Tag.ContentApplied: w.str(msg.collection).str(msg.version).str(msg.hash); break;
    case Tag.CropTint:
      w.svarint(msg.cx).svarint(msg.cz).svarint(msg.sy).varint(msg.cells.length);
      for (const c of msg.cells) w.varint(c.li).u8(c.q);
      break;
    case Tag.EntityName: w.varint(msg.id).str(msg.name); break;
    case Tag.NotebookUpdate:
      w.u8((msg.animals !== undefined ? 1 : 0) | (msg.crops !== undefined ? 2 : 0));
      if (msg.animals !== undefined) { w.varint(msg.animals.length); for (const a of msg.animals) writeAnimal(w, a); }
      if (msg.crops !== undefined) { w.varint(msg.crops.length); for (const c of msg.crops) writeCropRecord(w, c); }
      break;
    case Tag.Lesson:
      w.u8(LESSON_KINDS.indexOf(msg.kind)).u8(msg.text !== undefined ? 1 : 0);
      if (msg.text !== undefined) w.str(msg.text);
      break;
    case Tag.LessonUpdate:
      w.str(msg.lesson);
      writeLessonResult(w, msg.result);
      w.u8(msg.player !== undefined ? 1 : 0);
      if (msg.player !== undefined) w.varint(msg.player);
      break;
    case Tag.BlockText:
      w.svarint(msg.x).varint(msg.y + Y_BIAS).svarint(msg.z).u8(BLOCK_TEXT_KINDS.indexOf(msg.kind)).str(msg.payload);
      break;
    case Tag.BlockTextEdit:
      w.svarint(msg.x).varint(msg.y + Y_BIAS).svarint(msg.z).u8(BLOCK_TEXT_KINDS.indexOf(msg.kind)).str(msg.payload)
        .u8(msg.linked === true ? 1 : 0);
      break;
    case Tag.ShareState:
      w.svarint(msg.x).varint(msg.y + Y_BIAS).svarint(msg.z).varint(msg.sharer).bool(msg.live)
        .u8(msg.ice !== undefined ? 1 : 0);
      if (msg.ice !== undefined) {
        w.varint(msg.ice.length);
        for (const s of msg.ice) {
          w.str(s.urls).u8((s.username !== undefined ? 1 : 0) | (s.credential !== undefined ? 2 : 0));
          if (s.username !== undefined) w.str(s.username);
          if (s.credential !== undefined) w.str(s.credential);
        }
      }
      break;
    case Tag.Signal: w.varint(msg.from).varint(msg.to).str(msg.payload); break;
    case Tag.ShareStart: case Tag.ShareStop: case Tag.ShareJoin: case Tag.ShareLeave:
      w.svarint(msg.x).varint(msg.y + Y_BIAS).svarint(msg.z); break;
    default: {
      const _exhaustive: never = msg;
      throw new Error(`encode: unknown message ${JSON.stringify(_exhaustive)}`);
    }
  }
  return w.finish();
}

/** Parse wire bytes back into a message. Throws on an unknown tag or trailing bytes. */
export function decode(bytes: Uint8Array): NetMessage {
  const r = new ByteReader(bytes);
  const t = r.u8() as Tag;
  const msg = readBody(r, t);
  if (r.remaining !== 0) throw new Error(`decode: ${r.remaining} trailing byte(s) for tag ${t}`);
  return msg;
}

function readBody(r: ByteReader, t: Tag): NetMessage {
  switch (t) {
    case Tag.Hello:
      return { t, protocol: r.varint(), name: r.str(), hue: r.varint(), viewRadius: r.varint(), token: r.str() };
    case Tag.Input:
      return { t, seq: r.varint(), buttons: r.varint(), yaw: r.f64(), pitch: r.f64(), hotbar: r.u8() };
    case Tag.Edit:
      return { t, seq: r.varint(), x: r.svarint(), y: r.varint() - Y_BIAS, z: r.svarint(), state: r.varint(), place: r.bool() };
    case Tag.Txn: {
      const id = r.varint();
      const n = r.varint();
      const ops: TxnOp[] = [];
      for (let i = 0; i < n; i++) ops.push({ kind: r.u8(), container: r.str(), a: r.svarint(), b: r.svarint(), count: r.varint() });
      return { t, id, ops };
    }
    case Tag.Command: return { t, text: r.str() };
    case Tag.Chat: return { t, text: r.str() };
    case Tag.Ping: return { t, time: r.f64() };
    case Tag.Attack: return { t, seq: r.varint(), targetId: r.varint(), item: r.varint() };
    case Tag.Mount: return { t, seq: r.varint(), vehicleId: r.varint(), seat: r.u8() };
    case Tag.Ignite: return { t, seq: r.varint(), x: r.svarint(), y: r.varint() - Y_BIAS, z: r.svarint() };
    case Tag.Vehicle: return { t, seq: r.varint(), kind: r.str(), x: r.f64(), y: r.f64(), z: r.f64(), yaw: r.f64() };
    case Tag.ContentPush: return { t, collection: r.str(), version: r.str(), patch: r.str() };
    case Tag.Name: return { t, id: r.varint(), text: r.str(), item: r.varint() };
    case Tag.Record: {
      const kind = r.u8() === 0 ? 'animal' as const : 'crop' as const;
      const has = r.u8();
      const msg: { t: typeof Tag.Record; kind: 'animal' | 'crop'; id?: number; x?: number; y?: number; z?: number } = { t, kind };
      if (has & 1) msg.id = r.varint();
      if (has & 2) msg.x = r.svarint();
      if (has & 4) msg.y = r.svarint();
      if (has & 8) msg.z = r.svarint();
      return msg;
    }
    case Tag.Plant: return { t, x: r.svarint(), y: r.varint() - Y_BIAS, z: r.svarint(), q: r.u8() };
    case Tag.Hand: return { t, up: r.bool() };
    case Tag.QuizSubmit: {
      const lesson = r.str();
      const n = r.varint();
      const answers: QuizAnswerWire[] = [];
      for (let i = 0; i < n; i++) answers.push(readQuizAnswer(r));
      return { t, lesson, answers };
    }
    case Tag.Welcome: {
      const playerId = r.varint();
      const tick = r.varint();
      const meta = readMeta(r);
      const spawn = { x: r.f64(), y: r.f64(), z: r.f64(), yaw: r.f64(), pitch: r.f64(), dim: r.str() };
      return { t, playerId, tick, meta, spawn, viewRadius: r.varint() };
    }
    case Tag.Section:
      return { t, cx: r.svarint(), cz: r.svarint(), sy: r.u8(), palette: r.u16array(), indices: r.u16array() };
    case Tag.Patch: {
      const cx = r.svarint(); const cz = r.svarint(); const sy = r.u8();
      const n = r.varint();
      const ops: Array<{ li: number; state: number }> = [];
      for (let i = 0; i < n; i++) ops.push({ li: r.varint(), state: r.varint() });
      return { t, cx, cz, sy, ops };
    }
    case Tag.Unsub: return { t, cx: r.svarint(), cz: r.svarint() };
    case Tag.Spawn: {
      const n = r.varint();
      const entities = [];
      for (let i = 0; i < n; i++) {
        const e: { id: number; kind: string; x: number; y: number; z: number; yaw: number; genome?: string; name?: string } =
          { id: r.varint(), kind: r.str(), x: r.f64(), y: r.f64(), z: r.f64(), yaw: r.f64() };
        const has = r.u8();
        if (has & 1) e.genome = r.str();
        if (has & 2) e.name = r.str();
        entities.push(e);
      }
      return { t, entities };
    }
    case Tag.Move: {
      const n = r.varint();
      const entities = [];
      for (let i = 0; i < n; i++) entities.push({ id: r.varint(), x: r.f64(), y: r.f64(), z: r.f64(), yaw: r.f64(), pitch: r.f64() });
      return { t, entities };
    }
    case Tag.Despawn: {
      const n = r.varint();
      const ids: number[] = [];
      for (let i = 0; i < n; i++) ids.push(r.varint());
      return { t, ids };
    }
    case Tag.Correct:
      return { t, seq: r.varint(), x: r.f64(), y: r.f64(), z: r.f64(), vx: r.f64(), vy: r.f64(), vz: r.f64() };
    case Tag.TxnResult: return { t, id: r.varint(), ok: r.bool() };
    case Tag.ChatMsg: return { t, from: r.str(), text: r.str() };
    case Tag.Players: {
      const n = r.varint();
      const list: PlayerEntryWire[] = [];
      for (let i = 0; i < n; i++) {
        const e: PlayerEntryWire = { id: r.varint(), name: r.str(), hue: r.varint() };
        const has = r.u8();
        if (has & 1) e.role = r.str();
        if (has & 2) e.hand = r.varint();
        if (has & 4) e.status = r.str() as PlayerEntryWire['status'];
        list.push(e);
      }
      return { t, list };
    }
    case Tag.TimeWeather: return { t, dayTime: r.varint(), raining: r.bool(), thundering: r.bool() };
    case Tag.Pong: return { t, time: r.f64() };
    case Tag.Kick: return { t, reason: r.str() };
    case Tag.Pickup: return { t, item: r.varint(), count: r.varint() };
    case Tag.Dimension: return { t, dim: r.str(), x: r.f64(), y: r.f64(), z: r.f64() };
    case Tag.Riders: {
      const n = r.varint();
      const list = [];
      for (let i = 0; i < n; i++) list.push({ riderId: r.varint(), vehicleId: r.varint(), seat: r.u8() });
      return { t, list };
    }
    case Tag.ContentApplied: return { t, collection: r.str(), version: r.str(), hash: r.str() };
    case Tag.CropTint: {
      const cx = r.svarint(); const cz = r.svarint(); const sy = r.svarint();
      const n = r.varint();
      const cells: Array<{ li: number; q: number }> = [];
      for (let i = 0; i < n; i++) cells.push({ li: r.varint(), q: r.u8() });
      return { t, cx, cz, sy, cells };
    }
    case Tag.EntityName: return { t, id: r.varint(), name: r.str() };
    case Tag.NotebookUpdate: {
      const has = r.u8();
      const msg: { t: typeof Tag.NotebookUpdate; animals?: AnimalRecordWire[]; crops?: CropRecordWire[] } = { t };
      if (has & 1) { const n = r.varint(); msg.animals = []; for (let i = 0; i < n; i++) msg.animals.push(readAnimal(r)); }
      if (has & 2) { const n = r.varint(); msg.crops = []; for (let i = 0; i < n; i++) msg.crops.push(readCropRecord(r)); }
      return msg;
    }
    case Tag.Lesson: {
      const kind = LESSON_KINDS[r.u8()];
      if (kind === undefined) throw new Error('decode: unknown Lesson kind');
      const msg: { t: typeof Tag.Lesson; kind: LessonKind; text?: string } = { t, kind };
      if (r.u8() & 1) msg.text = r.str();
      return msg;
    }
    case Tag.LessonUpdate: {
      const lesson = r.str();
      const result = readLessonResult(r);
      const msg: { t: typeof Tag.LessonUpdate; lesson: string; result: LessonResultWire; player?: number } = { t, lesson, result };
      if (r.u8() & 1) msg.player = r.varint();
      return msg;
    }
    case Tag.BlockText: {
      const x = r.svarint(); const y = r.varint() - Y_BIAS; const z = r.svarint();
      const kind = BLOCK_TEXT_KINDS[r.u8()];
      if (kind === undefined) throw new Error('decode: unknown BlockText kind');
      return { t, x, y, z, kind, payload: r.str() };
    }
    case Tag.BlockTextEdit: {
      const x = r.svarint(); const y = r.varint() - Y_BIAS; const z = r.svarint();
      const kind = BLOCK_TEXT_KINDS[r.u8()];
      if (kind === undefined) throw new Error('decode: unknown BlockText kind');
      const payload = r.str();
      const linked = r.u8() & 1 ? true : undefined;
      return linked === undefined ? { t, x, y, z, kind, payload } : { t, x, y, z, kind, payload, linked };
    }
    case Tag.ShareState: {
      const x = r.svarint(); const y = r.varint() - Y_BIAS; const z = r.svarint();
      const sharer = r.varint(); const live = r.bool();
      const msg: { t: typeof Tag.ShareState; x: number; y: number; z: number; sharer: number; live: boolean; ice?: Array<{ urls: string; username?: string; credential?: string }> } = { t, x, y, z, sharer, live };
      if (r.u8() & 1) {
        const n = r.varint();
        msg.ice = [];
        for (let i = 0; i < n; i++) {
          const urls = r.str();
          const has = r.u8();
          const entry: { urls: string; username?: string; credential?: string } = { urls };
          if (has & 1) entry.username = r.str();
          if (has & 2) entry.credential = r.str();
          msg.ice.push(entry);
        }
      }
      return msg;
    }
    case Tag.Signal: return { t, from: r.varint(), to: r.varint(), payload: r.str() };
    case Tag.ShareStart: case Tag.ShareStop: case Tag.ShareJoin: case Tag.ShareLeave: {
      const x = r.svarint(); const y = r.varint() - Y_BIAS; const z = r.svarint();
      return { t, x, y, z };
    }
    default:
      throw new Error(`decode: unknown tag ${t}`);
  }
}

// ── Notebook record codecs (v9) ───────────────────────────────────────────────
const CAUSES = ['old_age', 'other'] as const;

function writeAnimal(w: ByteWriter, a: AnimalRecordWire): void {
  w.varint(a.id).str(a.species).str(a.genome).varint(a.parents[0]).varint(a.parents[1]).varint(a.born);
  w.u8((a.name !== undefined ? 1 : 0) | (a.died !== undefined ? 2 : 0) | (a.cause !== undefined ? 4 : 0));
  if (a.name !== undefined) w.str(a.name);
  if (a.died !== undefined) w.varint(a.died);
  if (a.cause !== undefined) w.u8(CAUSES.indexOf(a.cause));
}

function readAnimal(r: ByteReader): AnimalRecordWire {
  const a: AnimalRecordWire = {
    id: r.varint(), species: r.str(), genome: r.str(), parents: [r.varint(), r.varint()], born: r.varint(),
  };
  const has = r.u8();
  if (has & 1) a.name = r.str();
  if (has & 2) a.died = r.varint();
  if (has & 4) a.cause = CAUSES[r.u8()] ?? 'other';
  return a;
}

function writeCropRecord(w: ByteWriter, c: CropRecordWire): void {
  w.svarint(c.x).svarint(c.y).svarint(c.z).str(c.crop).str(c.genome).varint(c.harvests);
}

function readCropRecord(r: ByteReader): CropRecordWire {
  return { x: r.svarint(), y: r.svarint(), z: r.svarint(), crop: r.str(), genome: r.str(), harvests: r.varint() };
}

// ── Education codecs (v10) ────────────────────────────────────────────────────
export type QuizAnswerWire = z.infer<typeof zQuizAnswerWire>;

/** A number (u8 0 + f64) or a Punnett grid (u8 1 + rows, each a string list). */
function writeQuizAnswer(w: ByteWriter, a: QuizAnswerWire): void {
  if (typeof a === 'number') { w.u8(0).f64(a); return; }
  w.u8(1).varint(a.length);
  for (const row of a) { w.varint(row.length); for (const cell of row) w.str(cell); }
}

function readQuizAnswer(r: ByteReader): QuizAnswerWire {
  if (r.u8() === 0) return r.f64();
  const rows: string[][] = [];
  const n = r.varint();
  for (let i = 0; i < n; i++) {
    const m = r.varint();
    const row: string[] = [];
    for (let j = 0; j < m; j++) row.push(r.str());
    rows.push(row);
  }
  return rows;
}

/** A result row: objectives as (name, presence, tick) — `null` = not yet done — then
 *  the counters, ticks-on-task, and one presence byte over `quiz`/`completed`. */
function writeLessonResult(w: ByteWriter, res: LessonResultWire): void {
  w.varint(res.started);
  const objs = Object.entries(res.objectives);
  w.varint(objs.length);
  for (const [name, tick] of objs) {
    w.str(name).u8(tick === null ? 0 : 1);
    if (tick !== null) w.varint(tick);
  }
  w.varint(res.counts.length);
  for (const [k, v] of res.counts) w.str(k).varint(v);
  w.varint(res.ticksOnTask);
  w.u8((res.quiz !== undefined ? 1 : 0) | (res.completed !== undefined ? 2 : 0));
  if (res.quiz !== undefined) w.varint(res.quiz.best).varint(res.quiz.n).varint(res.quiz.attempts).varint(res.quiz.last);
  if (res.completed !== undefined) w.varint(res.completed);
}

function readLessonResult(r: ByteReader): LessonResultWire {
  const started = r.varint();
  const objectives: Record<string, number | null> = {};
  const n = r.varint();
  for (let i = 0; i < n; i++) {
    const name = r.str();
    objectives[name] = r.u8() & 1 ? r.varint() : null;
  }
  const counts: [string, number][] = [];
  const c = r.varint();
  for (let i = 0; i < c; i++) counts.push([r.str(), r.varint()]);
  const res: LessonResultWire = { started, objectives, counts, ticksOnTask: r.varint() };
  const has = r.u8();
  if (has & 1) res.quiz = { best: r.varint(), n: r.varint(), attempts: r.varint(), last: r.varint() };
  if (has & 2) res.completed = r.varint();
  return res;
}
